import React, { createContext, FC, ReactNode } from "react";
import { defineMessages, useIntl, IntlShape } from "react-intl";
import { ErrorController } from "@natera/platform/lib/hooks";
import { ResultCodes } from "@app/service/resultCodes";

export const errorMessages = defineMessages({
  invalidPasscode: {
    id: "invalidPasscode",
    defaultMessage: "Incorrect code.",
    description: "Error message if passcode is invalid",
  },
  codeInputWrongCodeMessage: {
    id: "codeInputWrongCodeMessage",
    defaultMessage: "Wrong verification code. Please try again.",
    description:
      "Error message if MFA verification code is incorrect (used for `CodeInput`/`VerifyCodeFormStepper` components)",
  },
});

// Helper function to set code verification errors - can be used directly by components
export const setCodeVerificationErrors = (
  errorController: ErrorController,
  intl: IntlShape
) => {
  errorController.setValidationError(
    "code",
    intl.formatMessage(errorMessages.invalidPasscode)
  );
  errorController.setValidationError(
    "codeStepper",
    intl.formatMessage(errorMessages.codeInputWrongCodeMessage)
  );
};

export enum MFAErrorCodes {
  PASSCODE_INVALID = "E0000068",
}

const POLL_STOPPED = "The poll was stopped by the sdk";

export interface MfaErrorController {
  parseError: (
    errorCode: string,
    errorMessage: string,
    errorController: ErrorController
  ) => Promise<void>;
  retranslateErrors: (errorController: ErrorController) => Promise<void>;
}

export const Context = createContext<MfaErrorController>({
  parseError: () => Promise.reject(),
  retranslateErrors: () => Promise.resolve(),
});

Context.displayName = "MfaErrorContext";

const MfaErrorProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const intl = useIntl();

  const parseError = async (
    errorCode: string,
    errorMessage: string,
    errorController: ErrorController
  ) => {
    switch (errorCode) {
      case MFAErrorCodes.PASSCODE_INVALID:
      case ResultCodes.INCORRECT_ACTIVATION_CODE:
        setCodeVerificationErrors(errorController, intl);
        break;
      default:
        if (errorMessage !== POLL_STOPPED) {
          errorController.setValidationError("code", errorMessage);
          errorController.setValidationError("codeStepper", errorMessage);
        }
        break;
    }
  };

  const retranslateErrors = async (errorController: ErrorController) => {
    const codeError = errorController.getValidationError("code");
    const codeStepperError = errorController.getValidationError("codeStepper");

    if (codeError || codeStepperError) {
      setCodeVerificationErrors(errorController, intl);
    }
  };

  return (
    <Context.Provider
      value={{
        parseError,
        retranslateErrors,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default MfaErrorProvider;
